<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

import type { ChangeValue } from "../sbi/protocol";
import type { TreeNode } from "../sbi/validate-render-op";
import InsightNode from "./InsightNode.vue";
import LucentIcon from "./LucentIcon.vue";
import { usePrintMode } from "../sbi/print-mode.ts";

const props = withDefaults(
  defineProps<{
    names: string[];
    tabs: Record<string, TreeNode[]>;
    /**
     * Optional per-tab icon name keyed by tab `name`. Authors who
     * pass bare strings to `sbi.ui.tabs(names, fn)` get no icons;
     * authors who pass `{ label, icon }` objects populate this map.
     */
    icons?: Record<string, string>;
    loading?: boolean;
    /** Per-pass field → format registry; forwarded to nodes inside the active tab. */
    formats?: Record<string, string>;
  }>(),
  {
    formats: () => ({}),
  },
);

defineEmits<{
  (e: "click-target", id: string, datum?: Record<string, unknown>, rowIndex?: number): void;
  (e: "change-target", id: string, value: ChangeValue): void;
  /** Forwarded from a descendant modal drilldown's X / Esc / backdrop. */
  (e: "chrome-click-target", id: string): void;
  /** Forwarded from descendant accordions so EmbedHost can surface the DOM event. */
  (e: "accordion-toggle", summary: string, open: boolean): void;
  /** Forwarded from descendant callouts so EmbedHost can surface the DOM event. */
  (e: "callout-rendered", kind: string, title?: string): void;
}>();

const active = ref(props.names[0]);

const isPrint = usePrintMode();

// The strip scrolls sideways when the tabs don't fit (never wraps —
// a wrapped strip's active underline only lines up on its last row);
// below 480px of container it stacks into a vertical list instead (CSS).
// Edge fades show there is more to scroll to; same shadow-toggle idea
// as InsightTable's horizontal scroll.
const stripEl = ref<HTMLElement | null>(null);
const canLeft = ref(false);
const canRight = ref(false);

const updateFades = (): void => {
  const el = stripEl.value;
  if (!el) return;
  canLeft.value = el.scrollLeft > 1;
  canRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
};

let observer: ResizeObserver | null = null;
onMounted(() => {
  updateFades();
  if (stripEl.value && typeof ResizeObserver !== "undefined") {
    observer = new ResizeObserver(() => updateFades());
    observer.observe(stripEl.value);
  }
});
onBeforeUnmount(() => observer?.disconnect());

watch(active, () => {
  void nextTick(() => {
    stripEl.value
      ?.querySelector<HTMLElement>("button.active")
      ?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  });
});
</script>

<template>
  <!--
    Print mode: render every tab stacked, each prefixed with its name
    as a section heading. Tabs are an on-screen affordance for
    information density; in a static PDF a tab the reader can't click
    is just hidden content. Stacking matches a board pack's "read top
    to bottom" flow.
  -->
  <div v-if="isPrint()" class="tabs tabs--print">
    <section
      v-for="name in names"
      :key="name"
      class="tab-print-section"
    >
      <h3 class="tab-print-heading">{{ name }}</h3>
      <InsightNode
        v-for="(child, i) in tabs[name]"
        :key="i"
        :node="child"
        :loading="loading"
        :formats="formats"
        @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
        @change-target="(id, value) => $emit('change-target', id, value)"
        @chrome-click-target="(id) => $emit('chrome-click-target', id)"
        @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
        @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
      />
    </section>
  </div>
  <div v-else class="tabs">
    <div
      ref="stripEl"
      class="tab-strip"
      :class="{ 'can-left': canLeft, 'can-right': canRight }"
      role="tablist"
      @scroll.passive="updateFades"
    >
      <button
        v-for="name in names"
        :key="name"
        type="button"
        role="tab"
        :title="name"
        :aria-selected="active === name"
        :class="{ active: active === name }"
        @click="active = name"
      >
        <LucentIcon
          v-if="icons?.[name]"
          :name="icons[name]"
          :size="14"
          class="tab-icon"
        />
        <span class="tab-label">{{ name }}</span>
      </button>
    </div>
    <div class="tab-body" role="tabpanel">
      <InsightNode
        v-for="(child, i) in tabs[active]"
        :key="i"
        :node="child"
        :loading="loading"
        :formats="formats"
        @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
        @change-target="(id, value) => $emit('change-target', id, value)"
        @chrome-click-target="(id) => $emit('chrome-click-target', id)"
        @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
        @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
      />
    </div>
  </div>
</template>

<style scoped>
/* `min-width: 0` on both the tabs container and the tab body lets the
 * flex chain shrink past the intrinsic min-content of charts inside —
 * without it, an ECharts canvas inside a tab measures against a parent
 * whose width was never resolved and falls back to its 100px default
 * (same failure mode as the standalone-chart case in EmbedHost). */
.tabs {
  display: flex;
  flex-direction: column;
  margin: var(--space-4) 0;
  min-width: 0;
}
.tab-strip {
  display: flex;
  flex-wrap: nowrap;
  gap: 2px;
  /* Inset shadow rather than a border: `overflow-x` would clip the
     buttons' old `margin-bottom: -1px` overlap. The active button's
     own 2px border paints over it. */
  box-shadow: inset 0 -1px 0 var(--rule);
  margin-bottom: 18px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
}
.tab-strip::-webkit-scrollbar {
  display: none;
}
.tab-strip.can-left {
  mask-image: linear-gradient(to right, transparent, #000 24px);
}
.tab-strip.can-right {
  mask-image: linear-gradient(to left, transparent, #000 24px);
}
.tab-strip.can-left.can-right {
  mask-image: linear-gradient(
    to right,
    transparent,
    #000 24px,
    #000 calc(100% - 24px),
    transparent
  );
}
.tab-body {
  min-width: 0;
  /* Container slot: nested components' @container queries measure
     this cell, not the page. Safe because the slot is stretched by
     its parent, never sized to its content. */
  container-type: inline-size;
}
.tab-strip button {
  appearance: none;
  background: transparent;
  border: none;
  padding: 10px 16px;
  font-family: var(--font);
  font-size: 13px;
  font-weight: 500;
  color: var(--card-muted);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  display: inline-flex;
  flex: 0 0 auto;
  max-width: min(22ch, 80cqi);
  align-items: center;
  gap: 6px;
  touch-action: manipulation;
  transition:
    color 120ms,
    border-color 120ms;
}
.tab-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (pointer: coarse) {
  .tab-strip button {
    min-height: 44px;
  }
}
/* Narrow (< 480px of the slot the tabs sit in — same breakpoint as
   columns' one-per-row): the strip stacks into a vertical list, one
   tab per row, so every tab stays visible instead of hiding behind a
   sideways scroll. The active marker moves to the left edge. */
@container (max-width: 479px) {
  .tab-strip {
    flex-direction: column;
    gap: 0;
    overflow-x: visible;
    box-shadow: inset 2px 0 0 var(--rule);
  }
  /* Matches each fade rule's specificity above. */
  .tab-strip.can-left,
  .tab-strip.can-right,
  .tab-strip.can-left.can-right {
    mask-image: none;
  }
  .tab-strip button {
    max-width: none;
    padding: 10px 14px;
    text-align: left;
    border-bottom: none;
    border-left: 2px solid transparent;
  }
  .tab-strip button.active {
    border-left-color: var(--primary);
  }
}
.tab-strip button .tab-icon {
  flex: 0 0 auto;
  color: currentColor;
  opacity: 0.85;
}
.tab-strip button:hover {
  color: var(--card-ink);
}
.tab-strip button.active {
  color: var(--card-ink);
  border-bottom-color: var(--primary);
  font-weight: 600;
}

/* Print mode — tabs stacked. Each section gets a heading that mimics
 * the on-screen tab-strip label so a reader of the PDF still sees
 * "Tab name → tab content" structure, just without the affordance to
 * click. `break-inside: avoid` keeps a tab's heading attached to its
 * first chunk of content where possible. */
.tabs--print {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: var(--space-4) 0;
  min-width: 0;
}
.tab-print-section {
  break-inside: avoid-page;
  min-width: 0;
  container-type: inline-size;
}
.tab-print-heading {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--card-muted);
  margin: 0 0 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--rule);
}
</style>
